/**
 * TermsPage.js - Terms & Conditions page for LeagueMaker
 * Accessible at /terms (public route, no auth required)
 *
 * NOTE: This is a template — have a lawyer review before going live with paid subscriptions.
 */

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import Logo from './Logo';
import './TermsPage.css';

const LAST_UPDATED = 'February 16, 2026';

const SECTIONS = [
  {
    title: '1. Acceptance of Terms',
    content: (
      <p>By creating an account or using the LeagueMaker platform, you agree to be bound by these Terms &amp; Conditions. If you do not agree to these terms, do not use the service. We may update these terms from time to time, and your continued use of the platform after changes are posted constitutes acceptance of the updated terms.</p>
    ),
  },
  {
    title: '2. Description of Service',
    content: (
      <>
        <p>LeagueMaker is a web-based platform for organizing recreational sports league schedules. The service includes:</p>
        <ul>
          <li>Automated schedule generation with fairness optimization</li>
          <li>Score tracking and standings management</li>
          <li>Public league pages and embeddable widgets</li>
          <li>Captain and referee portals with magic link access</li>
          <li>Email notifications for schedules, scores, and reminders</li>
          <li>PDF schedule export</li>
          <li>Playoff bracket management</li>
          <li>Season archiving and history</li>
        </ul>
        <p>Feature availability varies by subscription plan. See our <Link to="/pricing">Pricing page</Link> for details.</p>
      </>
    ),
  },
  {
    title: '3. User Accounts and Responsibilities',
    content: (
      <>
        <p>To use LeagueMaker, you must create an account with a valid email address and password. You are responsible for:</p>
        <ul>
          <li>Maintaining the confidentiality of your account credentials</li>
          <li>All activity that occurs under your account</li>
          <li>Providing accurate and up-to-date information</li>
          <li>Notifying us immediately of any unauthorized use of your account</li>
        </ul>
        <p>You must be at least 18 years old to create an account. By creating an account, you represent that you meet this age requirement.</p>
      </>
    ),
  },
  {
    title: '4. Acceptable Use Policy',
    content: (
      <>
        <p>You agree not to use LeagueMaker to:</p>
        <ul>
          <li>Violate any applicable laws or regulations</li>
          <li>Upload or transmit harmful, offensive, or illegal content</li>
          <li>Attempt to gain unauthorized access to other accounts or systems</li>
          <li>Interfere with or disrupt the platform or its infrastructure</li>
          <li>Use the service for any purpose other than managing recreational sports leagues</li>
          <li>Scrape, crawl, or use automated tools to extract data from the platform without permission</li>
          <li>Impersonate another person or entity</li>
        </ul>
        <p>We reserve the right to suspend or terminate accounts that violate this policy.</p>
      </>
    ),
  },
  {
    title: '5. Intellectual Property',
    content: (
      <>
        <p><strong>Your data belongs to you.</strong> All league data, team information, schedules, and scores you create on LeagueMaker remain your intellectual property. You may export your data at any time using the platform's export features.</p>
        <p><strong>Platform IP belongs to LeagueMaker.</strong> The LeagueMaker platform — including its design, code, algorithms, branding, and documentation — is the intellectual property of LeagueMaker. You may not copy, modify, distribute, or reverse-engineer any part of the platform without written permission.</p>
        <p>By using the service, you grant LeagueMaker a limited license to store, process, and display your data as necessary to provide the service. This license ends when you delete your data or close your account.</p>
      </>
    ),
  },
  {
    title: '6. Payment Terms and Refund Policy',
    content: (
      <>
        <p>LeagueMaker offers both free and paid subscription plans. For paid plans:</p>
        <ul>
          <li>Subscriptions are billed on a recurring basis (monthly or annually, as selected)</li>
          <li>Payments are processed securely through our payment provider (Stripe)</li>
          <li>You may cancel your subscription at any time from the Settings page</li>
          <li>Upon cancellation, you will retain access to paid features until the end of your current billing period</li>
          <li>Refunds may be issued at our discretion for billing errors or service issues — contact <a href="mailto:support@leaguemaker.app">support@leaguemaker.app</a> for assistance</li>
        </ul>
        <p>We reserve the right to change pricing with 30 days' notice to existing subscribers. Price changes will not affect your current billing period.</p>
      </>
    ),
  },
  {
    title: '7. Limitation of Liability',
    content: (
      <>
        <p>LeagueMaker is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not guarantee that the service will be uninterrupted, error-free, or free of harmful components.</p>
        <p>To the maximum extent permitted by law, LeagueMaker and its operators shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the platform, including but not limited to:</p>
        <ul>
          <li>Loss of data or scheduling information</li>
          <li>Errors in generated schedules or standings calculations</li>
          <li>Downtime or service interruptions</li>
          <li>Actions taken by other users of the platform</li>
        </ul>
        <p>Our total liability for any claim arising from your use of the service shall not exceed the amount you paid us in the 12 months prior to the claim.</p>
      </>
    ),
  },
  {
    title: '8. Termination of Accounts',
    content: (
      <>
        <p>You may delete your account at any time by contacting <a href="mailto:support@leaguemaker.app">support@leaguemaker.app</a>. Upon deletion, all your data (leagues, teams, schedules, scores) will be permanently removed from our systems.</p>
        <p>We may suspend or terminate your account if:</p>
        <ul>
          <li>You violate these Terms &amp; Conditions</li>
          <li>Your account has been inactive for an extended period</li>
          <li>We are required to do so by law</li>
          <li>We discontinue the service (with reasonable notice)</li>
        </ul>
        <p>If we terminate your account without cause, we will provide reasonable notice and an opportunity to export your data.</p>
      </>
    ),
  },
  {
    title: '9. Privacy',
    content: (
      <p>Your privacy is important to us. Please review our <Link to="/privacy">Privacy Policy</Link> for information about how we collect, use, and protect your personal data. By using LeagueMaker, you agree to the collection and use of information as described in our Privacy Policy.</p>
    ),
  },
  {
    title: '10. Changes to Terms',
    content: (
      <>
        <p>We may update these Terms &amp; Conditions from time to time. When we make changes, we will update the "Last updated" date at the top of this page. For significant changes, we may also notify you by email or through an in-app notice.</p>
        <p>Your continued use of LeagueMaker after changes are posted constitutes acceptance of the updated terms. If you do not agree with the changes, you should stop using the service and delete your account.</p>
      </>
    ),
  },
  {
    title: '11. Contact Information',
    content: (
      <>
        <p>If you have questions about these Terms &amp; Conditions, please contact us at:</p>
        <p><a href="mailto:support@leaguemaker.app">support@leaguemaker.app</a></p>
      </>
    ),
  },
];

export default function TermsPage() {
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const origRootBg = root.style.background;
    const origBodyBg = body.style.background;
    root.style.background = 'var(--color-bg)';
    body.style.background = 'var(--color-bg)';
    return () => {
      root.style.background = origRootBg;
      body.style.background = origBodyBg;
    };
  }, []);

  return (
    <div className="terms-page">
      <nav className="terms-nav">
        <Link to="/" className="terms-nav-logo">
          <Logo size="1.4rem" /> LeagueMaker
        </Link>
        <div className="terms-nav-actions">
          <button
            className="terms-theme-toggle"
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? '\u2600' : '\uD83C\uDF19'}
          </button>
          <Link to="/" className="terms-nav-link">Back to Home</Link>
        </div>
      </nav>

      <div className="terms-content">
        <div className="terms-hero">
          <h1 className="terms-hero-title">Terms &amp; Conditions</h1>
          <p className="terms-hero-subtitle">Last updated: {LAST_UPDATED}</p>
        </div>

        <div className="terms-notice">
          This is a template document. Please have a lawyer review these terms before going live with paid subscriptions.
        </div>

        {SECTIONS.map((section, index) => (
          <div key={index} className="terms-section">
            <h2 className="terms-section-title">{section.title}</h2>
            <div className="terms-section-body">
              {section.content}
            </div>
          </div>
        ))}
      </div>

      <footer className="terms-footer">
        <div className="terms-footer-links">
          <Link to="/privacy" className="terms-footer-link">Privacy Policy</Link>
          <span className="terms-footer-separator">|</span>
          <Link to="/help" className="terms-footer-link">Help</Link>
          <span className="terms-footer-separator">|</span>
          <Link to="/faq" className="terms-footer-link">FAQ</Link>
        </div>
        <p>&copy; {new Date().getFullYear()} LeagueMaker. All rights reserved.</p>
      </footer>
    </div>
  );
}
